import { PageHero, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/anim";

const RETAIL = [
  { name: "1-Day Pop-Up", price: "$250", note: "One day of in-person selling." },
  { name: "Weekend Takeover", price: "$450", note: "Friday through Sunday." },
  { name: "Retail Week", price: "$650", note: "7 consecutive days." },
  { name: "Monthly Retail Residency", price: "$850", note: "30-day retail residency." },
  { name: "Launch / Event Rental", price: "From $350", note: "Up to approximately 4 hours." },
];

const SWIS = [
  { name: "Rack Residency", price: "$250", unit: "/month" },
  { name: "Premium Rack + Display", price: "$350", unit: "/month" },
  { name: "Shelf Residency", price: "$150", unit: "/month" },
  { name: "Tabletop Display", price: "$125", unit: "/month" },
  { name: "Window Feature", price: "$200", unit: "/week" },
];

const BEAUTY = [
  { name: "Makeup Chair", day: "$50", week: "$225", month: "$650" },
  { name: "Nail Station", day: "$60", week: "$275", month: "$750" },
  { name: "Hair Chair", day: "$75", week: "$325", month: "$850" },
];

function PriceCard({ p, i, group }) {
  return (
    <Reveal delay={i * 0.05}>
      <div
        className="flex h-full flex-col rounded-3xl border border-beige/60 bg-white/80 p-8 shadow-[0_4px_24px_rgba(26,24,23,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,24,23,0.1)]"
        data-testid={`pricing-card-${group}-${i + 1}`}
      >
        <h4 className="font-serif text-xl uppercase text-ink">{p.name}</h4>
        <p className="mt-5 font-serif text-4xl text-ink">
          {p.price}
          {p.unit && <span className="text-lg italic text-sanddeep">{p.unit}</span>}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-clay">{p.note}</p>
      </div>
    </Reveal>
  );
}

export default function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        lines={[
          <>Clear pricing.</>,
          <>
            <em>Beautiful space.</em>
          </>,
        ]}
        sub="Flexible rentals for retail, shop-within-a-shop, and beauty professionals — priced so you can plan your moment."
      />

      <section className="bg-paper pb-24 sm:pb-32" data-testid="pricing-retail">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] text-fog">Retail</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {RETAIL.map((p, i) => (
              <PriceCard key={p.name} p={p} i={i} group="retail" />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 sm:py-32" data-testid="pricing-swis">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] text-fog">Shop Within a Shop</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SWIS.map((p, i) => (
              <PriceCard key={p.name} p={p} i={i} group="swis" />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 sm:py-32" data-testid="pricing-beauty">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] text-fog">Beauty</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {BEAUTY.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.06}>
                <div
                  className="rounded-3xl border border-beige/60 bg-white/80 p-8 shadow-[0_4px_24px_rgba(26,24,23,0.04)]"
                  data-testid={`pricing-card-beauty-${i + 1}`}
                >
                  <h4 className="font-serif text-xl uppercase text-ink">{b.name}</h4>
                  <ul className="mt-6 space-y-3">
                    {[
                      ["Day", b.day],
                      ["Week", b.week],
                      ["Month", b.month],
                    ].map(([k, v]) => (
                      <li
                        key={k}
                        className="flex items-baseline justify-between border-b border-beige/50 pb-3 last:border-none"
                      >
                        <span className="text-[11px] uppercase tracking-[0.22em] text-fog">{k}</span>
                        <span className="font-serif text-2xl text-ink">{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-12 max-w-2xl rounded-2xl border border-blushdeep bg-blush/50 p-6 text-sm leading-relaxed text-clay" data-testid="pricing-note">
              Pricing and availability are subject to change. Certain services, equipment,
              utilities, and special setups may incur additional fees.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand
        title={
          <>
            Found your fit?
            <br />
            <em>Come and get it.</em>
          </>
        }
        cta="Check Availability"
        to="/book"
        testid="pricing-cta-button"
      />
    </>
  );
}
